import i18n from '@/i18n/index.ts'

function getPrimaryMessage(error: unknown): string {
  if (error instanceof Error) return error.message
  if (typeof error === 'string') return error
  return String(error)
}

/** Flatten viem/wagmi error trees into one lowercase string for pattern matching. */
function collectErrorText(error: unknown): string {
  const parts: string[] = []
  const seen = new Set<unknown>()

  const walk = (x: unknown): void => {
    if (x == null || seen.has(x)) return
    if (typeof x === 'object') seen.add(x)

    if (typeof x === 'string') {
      parts.push(x)
      return
    }
    if (x instanceof Error) {
      parts.push(x.message)
      walk((x as Error & { cause?: unknown }).cause)
      return
    }
    if (typeof x === 'object' && x !== null) {
      const o = x as Record<string, unknown>
      for (const k of ['shortMessage', 'details', 'message']) {
        const v = o[k]
        if (typeof v === 'string') parts.push(v)
      }
      const meta = o.metaMessages
      if (Array.isArray(meta)) {
        for (const item of meta) {
          if (typeof item === 'string') parts.push(item)
        }
      }
    }
  }

  walk(error)
  return parts.join('\n').toLowerCase()
}

function isUserRejected(combined: string): boolean {
  return (
    combined.includes('user rejected') ||
    combined.includes('user denied') ||
    combined.includes('rejected the request') ||
    combined.includes('denied transaction signature') ||
    combined.includes('action_rejected') ||
    combined.includes('4001') ||
    combined.includes('rejected the transaction') ||
    combined.includes('request rejected') ||
    combined.includes('user cancelled') ||
    combined.includes('user canceled')
  )
}

function isInsufficientFunds(combined: string): boolean {
  return (
    combined.includes('insufficient funds') ||
    combined.includes('insufficient balance') ||
    combined.includes('exceeds the balance') ||
    combined.includes('transfer amount exceeds balance') ||
    combined.includes('overshot') ||
    combined.includes('gas required exceeds allowance') ||
    combined.includes('intrinsic gas too low') ||
    combined.includes('max fee per gas less than block base fee')
  )
}

function isWrongNetwork(combined: string): boolean {
  return (
    combined.includes('wrong network') ||
    combined.includes('chain mismatch') ||
    combined.includes('unsupported chain') ||
    combined.includes('invalid chain') ||
    combined.includes('switch network') ||
    combined.includes('chain not configured')
  )
}

function isNonceIssue(combined: string): boolean {
  return (
    combined.includes('nonce too low') ||
    combined.includes('nonce has already been used') ||
    combined.includes('replacement transaction') ||
    combined.includes('replacement fee too low') ||
    combined.includes('tx already in queue')
  )
}

function isReverted(combined: string): boolean {
  return (
    combined.includes('execution reverted') ||
    combined.includes('transaction reverted') ||
    combined.includes('revert') ||
    combined.includes('erc20:') ||
    combined.includes('transfer amount exceeds allowance')
  )
}

/**
 * Maps wallet / on-chain errors (viem, MetaMask, etc.) to short, translated copy.
 * Avoids dumping long "Request Arguments" / ABI traces into the UI.
 */
export function getFriendlyTransactionError(error: unknown): string {
  const combined = collectErrorText(error)
  const primary = getPrimaryMessage(error)

  if (isUserRejected(combined)) {
    return i18n.t('walletErrors.userRejected')
  }
  if (isInsufficientFunds(combined)) {
    return i18n.t('walletErrors.insufficientFunds')
  }
  if (isWrongNetwork(combined)) {
    return i18n.t('walletErrors.wrongNetwork')
  }
  if (isNonceIssue(combined)) {
    return i18n.t('walletErrors.nonce')
  }
  if (isReverted(combined)) {
    return i18n.t('walletErrors.reverted')
  }

  const noisy =
    primary.length > 400 ||
    combined.includes('request arguments') ||
    combined.includes('contract call:') ||
    combined.includes('viem@') ||
    combined.includes('details: metamask') ||
    combined.includes('signature:')

  if (noisy) {
    return i18n.t('walletErrors.generic')
  }

  const trimmed = primary.trim()
  if (trimmed.length > 320) {
    return i18n.t('walletErrors.generic')
  }
  return trimmed || i18n.t('walletErrors.generic')
}
